import { Schema as S } from "effect";

import { defineApi } from "../client";
import { Page } from "../schemas/common";
import {
	ProfileImageExists,
	RecordHistory,
	RecordSummary,
	SearchResult,
} from "../schemas/records";

const AsOf = S.NullOr(S.String);

/** v1 `useRecords`: `asOf` set hits the `as_of` form (no search, no sort: the server rejects both with `as_of`), as the hook did. */
export const getRecords = defineApi("GetRecords", {
	args: {
		search: S.String,
		cursor: S.Number,
		asOf: AsOf,
		/** `recent` orders by newest linked entry (KTD6); absent means id order. */
		sort: S.optionalKey(S.Literals(["recent"])),
		/** Page size; absent means the server default. */
		limit: S.optionalKey(S.Number),
	},
	response: Page(RecordSummary),
	request: ({ search, cursor, asOf, sort, limit }) => ({
		method: "GET",
		path: "/records",
		query: asOf ? { as_of: asOf, cursor } : { search, cursor, sort, limit },
	}),
});

export const getSearch = defineApi("GetSearch", {
	args: {
		search: S.String,
		cursor: S.Number,
		searchType: AsOf,
		knotType: AsOf,
		tags: S.Array(S.String),
	},
	response: Page(SearchResult),
	request: ({ search, cursor, searchType, knotType, tags }) => ({
		method: "GET",
		path: "/search",
		query: {
			search,
			cursor,
			type: searchType === "all" ? null : searchType,
			knotType,
			tags,
		},
	}),
});

/** v1 `useRecord`: `asOf` set hits the `as_of` form. */
export const getRecord = defineApi("GetRecord", {
	args: { nodeId: S.String, asOf: AsOf },
	response: RecordSummary,
	request: ({ nodeId, asOf }) => ({
		method: "GET",
		path: `/records/${nodeId}`,
		query: { as_of: asOf },
	}),
});

export const addRecord = defineApi("AddRecord", {
	args: { data: S.Unknown },
	response: S.Unknown,
	request: ({ data }) => ({ method: "POST", path: "/records", body: data }),
});

export const deleteRecord = defineApi("DeleteRecord", {
	args: { nodeId: S.String },
	response: S.Unknown,
	request: ({ nodeId }) => ({ method: "DELETE", path: `/records/${nodeId}` }),
});

/** With `ifLastUpdated`, the server answers 412 if the record changed since that read. */
export const editRecord = defineApi("EditRecord", {
	args: {
		nodeId: S.String,
		record: RecordSummary,
		ifLastUpdated: S.optionalKey(S.NullOr(S.String)),
	},
	response: S.Unknown,
	request: ({ nodeId, record, ifLastUpdated }) => ({
		method: "PATCH",
		path: `/records/${nodeId}`,
		query: { ifLastUpdated },
		body: record,
	}),
});

/** `target` remains; `source` is merged into it and deleted. */
export const mergeRecords = defineApi("MergeRecords", {
	args: { targetNodeId: S.String, sourceNodeId: S.String },
	response: S.Unknown,
	request: ({ targetNodeId, sourceNodeId }) => ({
		method: "POST",
		path: "/records/merge",
		body: { targetNodeId, sourceNodeId },
	}),
});

/** v1 `ProfileImageUploader`: FormData field `image`, filename `avatar.jpg`. */
export const uploadProfileImage = defineApi("UploadProfileImage", {
	args: { nodeId: S.String, image: S.instanceOf(Blob) },
	response: S.Unknown,
	request: ({ nodeId, image }) => {
		const body = new FormData();
		body.append("image", image, "avatar.jpg");
		return { method: "POST", path: `/records/${nodeId}/profile-image`, body };
	},
});

/** U3: `GET /records/lookup?system=linkedin&value=`; a miss is the typed `NotFound` (KTD4). */
export const lookupRecord = defineApi("LookupRecord", {
	args: { system: S.Literal("linkedin"), value: S.String },
	response: RecordSummary,
	request: ({ system, value }) => ({
		method: "GET",
		path: "/records/lookup",
		query: { system, value },
	}),
});

/** U3: the server downloads the photo from an allowlisted host and stores it as the avatar (KTD5). */
export const importProfileImageFromUrl = defineApi(
	"ImportProfileImageFromUrl",
	{
		args: { nodeId: S.String, url: S.String },
		response: S.Unknown,
		request: ({ nodeId, url }) => ({
			method: "POST",
			path: `/records/${nodeId}/profile-image/from-url`,
			body: { url },
		}),
	},
);

export const checkProfileImageExists = defineApi("CheckProfileImageExists", {
	args: { nodeId: S.String },
	response: ProfileImageExists,
	request: ({ nodeId }) => ({
		method: "GET",
		path: `/records/${nodeId}/profile-image/exists`,
	}),
});

export const getRecordHistory = defineApi("GetRecordHistory", {
	args: { nodeId: S.String, cursor: S.Number },
	response: RecordHistory,
	request: ({ nodeId, cursor }) => ({
		method: "GET",
		path: `/records/${nodeId}/history`,
		query: { cursor, limit: 20 },
	}),
});

/** L5: someone else's agent link on a person record; `null` clears it. Its own write, never part of PATCH. */
export const setRecordAgentLink = defineApi("SetRecordAgentLink", {
	args: { nodeId: S.String, url: S.NullOr(S.String) },
	response: S.Unknown,
	request: ({ nodeId, url }) =>
		url === null
			? { method: "DELETE", path: `/agents/record-link/${nodeId}` }
			: { method: "PUT", path: `/agents/record-link/${nodeId}`, body: { url } },
});
